import styled, { css } from 'styled-components';
import { device } from 'app/ui-components/breakpoints';
import {
    accentGreen,
    fontWeightBold,
    fontWeightRegular,
    iconClose,
    regularBlack,
    regularWhite,
} from 'app/ui-components/design-tokens';
import { Heading2Typography, Heading5Typography } from 'app/ui-components/typography/Heading.styles';
import { playFontIcon, playFontSize, regularOutline } from 'app/ui-components/typography/helpers';
import { PlayTextStyles } from 'app/ui-components/typography/PlayText/PlayText.styles';
import { PlayFocus, VisuallyHidden } from 'app/ui-components/utils.styles';
import { StyledButton } from 'app/ui-components/PlayButton/PlayButton.styles';
import { TYPES } from 'app/ui-components/typography/PlayText/PlayText';
import { ExternalText } from 'app/components/Link/Link.styles';

export const StyledParentalControl = styled.div`
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    text-align: center;
    z-index: 10;
    pointer-events: auto;
`;

export const StyledParentalControlInner = styled.div`
    position: absolute;
    top: 50%;
    width: 100%;
    transform: translateY(-50%);

    .ie9 & {
        transform: translateY(0);
    }

    &:focus {
        outline: none;
    }

    @media ${device.GteSmall} {
        margin: 0 10%;
        width: 82%;
    }

    @media ${device.GteMedium} {
        margin: 0;
        width: 100%;
    }
`;

export const StyledParentalControlHeading = styled.h2`
    ${Heading5Typography}
    color: ${regularWhite};
    margin-bottom: 5px;

    @media ${device.GteSmall} {
        margin-bottom: 10px;
    }

    @media ${device.GteLarge} {
        ${playFontSize(2.6)}
    }
`;

export const ParentalControlDefaultLabel = styled.label`
    ${PlayTextStyles}
    user-select: none;
    cursor: pointer;
    position: relative;
    display: inline-block;
    margin-top: 4px;
    margin-bottom: 10px;

    @media ${device.GteSmall} {
        margin-bottom: 20px;
    }
`;

export const ParentalControlDefaultInputWrapper = styled.span`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const ParentalControlDefaultCheckMark = styled.span`
    width: 16px;
    height: 16px;
    display: inline-block;
    position: relative;

    background: ${regularWhite};
    margin-right: 7px;
    border-radius: 2px;

    ${(props) =>
        props.isChecked &&
        css`
            &:before {
                position: absolute;
                top: -1px;
                left: -1px;
                ${playFontIcon(1.8, iconClose)}
            }

            background: ${accentGreen};
            color: ${regularWhite};
        `}
`;

export const ParentalControlDefaultInput = styled.input.attrs({ type: 'checkbox' })`
    ${VisuallyHidden}

    &:focus ~ ${ParentalControlDefaultCheckMark} {
        ${regularOutline(2)}
    }

    &:focus-visible ~ ${ParentalControlDefaultCheckMark} {
        ${regularOutline(2)}
    }

    &:focus:not(:focus-visible) ~ ${ParentalControlDefaultCheckMark} {
        outline: none;
    }
`;

export const ParentalControlDefaultButtons = styled.div`
    margin: 15px 15px 0;

    @media ${device.GteSmall} {
        margin: 15px auto 0;
        max-width: 290px;
    }
`;

export const ParentalControlDefaultButton = styled(StyledButton).attrs({ theme: 'light' })`
    margin-bottom: 20px;
    padding: 9px 20px;
    width: 100%;

    ${(props) =>
        props.isDisabled &&
        css`
            pointer-events: none;
        `}
`;

export const StyledParentalControlLock = styled.div`
    position: relative;
    margin-bottom: 15px;

    @media ${device.GteSmall} {
        margin-bottom: 30px;
    }
`;

export const StyledParentalControlLockLabel = styled.label`
    ${Heading5Typography}
    font-weight: ${fontWeightRegular};
    color: ${regularWhite};
    display: block;
    line-height: 1;
    margin-bottom: 20px;

    @media ${device.GteMedium} {
        margin-bottom: 35px;
    }
`;

export const StyledParentalControlLockCode = styled.input`
    ${Heading2Typography}
    border-radius: 20px;
    color: ${regularBlack};
    width: 80px;
    background-color: ${regularWhite};
    padding: 0 10px;
    border: 2px solid transparent;
    line-height: 1;
    text-align: center;
    vertical-align: middle;
    margin-right: 10px;

    ${PlayFocus}

    /* stylelint-disable selector-no-vendor-prefix */
    &::-webkit-input-placeholder {
        padding-top: 2px;
    }
    /* stylelint-enable selector-no-vendor-prefix */

    @media ${device.GteSmall} {
        border-radius: 40px;
        width: 125px;
    }

    @media ${device.GteMedium} {
        width: 135px;
    }
`;

export const StyledParentalControlLockButton = styled(StyledButton).attrs({ theme: 'light' })`
    display: inline-block;

    ${(props) =>
        props.isDisabled &&
        css`
            pointer-events: none;
        `}
`;

export const StyledParentalControlLockForgot = styled.div.attrs({ type: TYPES.SMALL })`
    display: inline-block;

    ${ExternalText} {
        text-underline-position: under;
    }

    ${PlayTextStyles}
`;

export const StyledParentalControlLockInvalid = styled.p.attrs({ type: TYPES.SMALL })`
    ${PlayTextStyles}
    color: ${regularWhite};
    font-weight: ${fontWeightBold};
    margin-top: 10px;
`;
